import { Link, useLocation } from '@solidjs/router';
import { createSignal, Show, useContext } from 'solid-js';
import clsx from 'clsx';
import { AppContext } from '../../app-context-provider/app-context-provider';
import { ErrorCatcher } from '../../tools/error-catcher';
import { getImageServiceSrcPath } from '../image-component/image-component';
import { CardFavouriteButton } from '../my-content/card-favourite-button';
import { LockedContent } from '../../ui-components/layouts/locked-content';
import { EditorialCardProps } from './editorial-card-types';
import decodeHtmlEntities from '../../tools/decode-html-entities';
import { removeTrailingSlash } from '../../tools/remove-trailing-slash';
import IntersectionObserver from '../intersection-observer/intersection-observer';

const SRCSET_WIDTHS = [480, 600, 750, 960, 1200, 1500];

export const EditorialCard = (props: EditorialCardProps) => {
    const { imagesServiceUrl, supportedImageFormats, siteInfo, userState, pageRegistration } = useContext(AppContext);
    const [isVisible, setIsVisible] = createSignal(false);

    const isHiddenForPublic = () =>
        props.data.isHealthcareProfessional
            && !props.data.isVisibleForPublic
            && !userState.user
            && pageRegistration.url !== '';

    const opensInNewTab = () => props.data.url?.includes('http');

    const onCardVisible = (visible: boolean | unknown) => {
        if (visible) {
            setIsVisible(true);
        }
    };

    const myContentEnabled = () => siteInfo.siteType === 'default';

    if (!props.data?.url) {
        return null;
    }

    const buildImageSrc = (width: number) => {
        if (!props.data.listCardContent?.image?.url) return '';
        return getImageServiceSrcPath({
            imagesServiceUrl,
            supportedImageFormats,
            src: props.data.listCardContent.image.url,
            width,
            height: 'AUTO',
        });
    };

    const srcset = () => SRCSET_WIDTHS.map((w) => `${buildImageSrc(w)} ${w}w`).join(', ');

    // 1x1 render of the image — browser stretches it into a dominant-color wash that
    // sits behind the full image until the IntersectionObserver swaps in the hi-res.
    const lowResImageSrc = () => {
        if (!props.data.listCardContent?.image?.url) return '';
        return getImageServiceSrcPath({
            imagesServiceUrl,
            supportedImageFormats,
            src: props.data.listCardContent.image.url,
            width: 1,
            height: 1,
        });
    };

    const categoryLabel = () => props.categoryLabel ?? props.data.categories?.[0]?.name;

    const excerptVisibilityClass = () => {
        if (props.showExcerpt === true) {
            return '';
        }
        if (props.showExcerpt === false) {
            return 'hidden';
        }
        return 'tablet:hidden';
    };

    return (
        <ErrorCatcher componentName="EditorialCard">
            <IntersectionObserver onVisible={onCardVisible} threshold={0.1} oneTime>
                <Link
                    href={isHiddenForPublic() ? useLocation().pathname : removeTrailingSlash(props.data.url)}
                    target={opensInNewTab() ? '_blank' : undefined}
                    class={clsx(
                        'group relative flex flex-col gap-cvi-6 overflow-hidden rounded-cvi-md no-underline transition-opacity duration-200',
                        isHiddenForPublic() && 'pointer-events-none',
                        props.stale && 'opacity-60',
                    )}
                >
                    <div class="relative aspect-[29/16] w-full overflow-hidden bg-cvi-bg-neutral-subtle rounded-cvi-md">
                        <Show when={props.data.listCardContent?.image?.url} fallback={props.mediaFallback}>
                            <Show when={isVisible()}>
                                <img
                                    src={buildImageSrc(750)}
                                    srcset={srcset()}
                                    sizes="(min-width: 1270px) 25vw, (min-width: 768px) 33vw, 100vw"
                                    alt={props.data.listCardContent?.image?.altText ?? ''}
                                    class="absolute inset-0 size-full object-cover object-center z-0"
                                    loading="lazy"
                                />
                            </Show>
                            <img
                                src={lowResImageSrc()}
                                alt=""
                                aria-hidden="true"
                                class="absolute inset-0 size-full object-cover object-center z-[1]"
                                style={isVisible() ? { animation: 'image-fade-out 0.8s ease-out forwards' } : undefined}
                            />
                        </Show>

                        <Show when={categoryLabel()}>
                            <span class="absolute right-cvi-6 top-cvi-6 z-[2] rounded-cvi-sm bg-cvi-bg-interactive-strong px-cvi-4 py-cvi-2 label-sm font-cvi-medium text-cvi-fg-on-color">
                                {decodeHtmlEntities(categoryLabel())}
                            </span>
                        </Show>

                        <Show when={myContentEnabled()}>
                            <CardFavouriteButton
                                class="absolute right-cvi-6 bottom-cvi-6 z-[2]"
                                title={props.data.listCardContent?.title ?? ''}
                                permalink={props.data.permalink}
                                type={props.myContentType ?? 'blog-and-news'}
                            />
                        </Show>
                    </div>

                    <div class="flex flex-col gap-cvi-4">
                        <Show when={props.eyebrow}>
                            <span class="body-xs font-cvi-regular text-cvi-fg-muted">{props.eyebrow}</span>
                        </Show>
                        <Show when={props.data.listCardContent?.title}>
                            <h3 class="heading-lg desktop:text-heading-md desktop:leading-heading-md font-cvi-medium text-cvi-fg-default m-0">
                                {decodeHtmlEntities(props.data.listCardContent?.title)}
                            </h3>
                        </Show>
                        <Show when={props.data.listCardContent?.cardText}>
                            <p class={clsx('body-sm font-cvi-regular text-cvi-fg-muted m-0 line-clamp-2', excerptVisibilityClass())}>
                                {props.data.listCardContent!.cardText}
                            </p>
                        </Show>
                    </div>
                    <Show when={isHiddenForPublic()}>
                        <LockedContent isColumn={true} />
                    </Show>
                </Link>
            </IntersectionObserver>
        </ErrorCatcher>
    );
};
